import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaUsers,
  FaUserFriends,
  FaBuilding,
  FaHome,
  FaFileInvoice,
  FaBook,
  FaCheck,
  FaHandshake,
  FaInfoCircle,
  FaBell,
  FaEnvelope,
} from "react-icons/fa";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";

import SettingsGroups from "./SettingsGroups";
import SettingsDojos from "./SettingsDojos";
import SettingsForm from "./SettingsForm";
import HomeSettingsForm from "./HomeSettingsForm";
import PaiementSettingsForm from "./PaiementSettingsForm";
import TeamManagement from "./TeamTable";
import DisciplinesTable from "./DisciplinesTable";
import InfoClubForm from "./InfoClubForm";
import ModuleSettingsForm from "./ModuleSettingsForm";
import ReglementInterieurForm from "./ReglementInterieurForm";
import SettingsPartenaires from "./SettingsPartenaires";
import SettingsDossierInscription from "./SettingsDossierInscription";
import SettingsNotifications from "./SettingsNotifications";
import SettingsMessages from "./SettingsMessages";
import { ToastContainer } from "react-toastify";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

const SettingsPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const tabs = useMemo(
    () => [
      { key: "dojos", label: "Dojos", Icon: FaUsers, Component: SettingsDojos },
      {
        key: "groupes",
        label: "Groupes",
        Icon: FaUsers,
        Component: SettingsGroups,
      },
      {
        key: "equipes",
        label: "Équipes",
        Icon: FaUserFriends,
        Component: TeamManagement,
      },
      {
        key: "disciplines",
        label: "Disciplines",
        Icon: FaBook,
        Component: DisciplinesTable,
      },
      {
        key: "association",
        label: "Association",
        Icon: FaBuilding,
        Component: SettingsForm,
      },
      {
        key: "pageAccueil",
        label: "Page Accueil",
        Icon: FaHome,
        Component: HomeSettingsForm,
      },
      {
        key: "pageInfoClub",
        label: "Info Club",
        Icon: FaInfoCircle,
        Component: InfoClubForm,
      },
      {
        key: "dossier",
        label: "Dossier Inscription",
        Icon: FaFileInvoice,
        Component: SettingsDossierInscription,
      },
      {
        key: "paiements",
        label: "Paiements",
        Icon: FaFileInvoice,
        Component: PaiementSettingsForm,
      },
      {
        key: "reglementInterieur",
        label: "Règlement Intérieur",
        Icon: FaFileInvoice,
        Component: ReglementInterieurForm,
      },
      {
        key: "partenaires",
        label: "Partenaires",
        Icon: FaHandshake,
        Component: SettingsPartenaires,
      },
      {
        key: "notifications",
        label: "Notifications",
        Icon: FaBell,
        Component: SettingsNotifications,
      },
      {
        key: "messages",
        label: "Messages",
        Icon: FaEnvelope,
        Component: SettingsMessages,
      },
      {
        key: "modules",
        label: "Modules",
        Icon: FaCheck,
        Component: ModuleSettingsForm,
      },
    ],
    []
  );

  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("settings_active_tab") || "groupes";
  });

  useEffect(() => {
    localStorage.setItem("settings_active_tab", activeTab);
  }, [activeTab]);

  const ActiveComponent =
    tabs.find((t) => t.key === activeTab)?.Component ?? SettingsGroups;

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-3 sm:px-6 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] mb-4">
        {/* Header gradient */}
        <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
          <HeaderTitle
            role="Administrateur"
            title="Paramètres du club"
            align="center"
            className="mb-1"
          />
          <div className="mt-5">
            <DashboardNavigation role={userRole} />
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* Tabs controls */}
          <div className="border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80">
            {/* MOBILE: liste déroulante */}
            <div className="sm:hidden px-3 py-3">
              <label
                htmlFor="settings-tab-select"
                className="block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-1"
              >
                Section
              </label>
              <div className="relative">
                <select
                  id="settings-tab-select"
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value)}
                  className="w-full appearance-none rounded-xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 px-3 py-2 pr-9 text-sm font-medium text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  {tabs.map(({ key, label }) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
                {/* Chevron */}
                <svg
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 dark:text-slate-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>

            {/* DESKTOP/TABLET: toutes les tabs visibles, sur 1 à 3 lignes (flex-wrap) */}
            <nav
              className="hidden sm:flex flex-wrap items-center gap-2 px-3 sm:px-4 py-3"
              role="tablist"
              aria-label="Paramètres du club"
            >
              {tabs.map(({ key, label, Icon }) => {
                const active = activeTab === key;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    role="tab"
                    aria-selected={active}
                    aria-controls={`panel-${key}`}
                    id={`tab-${key}`}
                    className={[
                      "group relative inline-flex items-center gap-2",
                      "px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg transition",
                      "ring-1",
                      active
                        ? "bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white ring-transparent shadow"
                        : "bg-white/60 dark:bg-slate-900/60 text-slate-700 dark:text-slate-200 ring-slate-200 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60",
                    ].join(" ")}
                    title={label}
                  >
                    <Icon
                      className={
                        active
                          ? "text-white"
                          : "text-slate-500 dark:text-slate-300 group-hover:text-slate-700 dark:group-hover:text-white"
                      }
                    />
                    <span className="whitespace-nowrap text-sm font-medium">
                      {label}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div
            id={`panel-${activeTab}`}
            role="tabpanel"
            aria-labelledby={`tab-${activeTab}`}
            className="p-4 sm:p-6"
          >
            <div>
              <ActiveComponent />
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />

      {/* Local styles: smooth text on Apple */}
      <style>{`
        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </section>
  );
};

export default SettingsPage;
